import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useOpenSolarSync } from '@/hooks/use-opensolar-sync';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Calendar,
  Database,
  ExternalLink,
  Filter,
  Info,
  RefreshCw,
  Search,
  Settings,
  Zap,
} from 'lucide-react';

export default function OpenSolarProjectsPage() {
  const [location, navigate] = useLocation();
  const { projects, isLoadingProjects, triggerSync, isSyncing, refetchProjects, syncStats } =
    useOpenSolarSync();
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  // Initial load - fetch projects if none are present
  useEffect(() => {
    if (!isLoadingProjects && projects.length === 0) {
      // If there are no projects, automatically trigger a sync
      triggerSync()
        .then(() => {
          toast({
            title: 'Sync Initiated',
            description: 'Fetching all OpenSolar projects. This may take a minute.',
          });
        })
        .catch((error) => {
          console.error('Error syncing projects:', error);
        });
    }
  }, [isLoadingProjects, projects.length]);

  // Filter projects based on search term
  const filteredProjects = projects.filter((project) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (project.name ? project.name.toLowerCase().includes(searchLower) : false) ||
      (project.customerName ? project.customerName.toLowerCase().includes(searchLower) : false) ||
      (project.address ? project.address.toLowerCase().includes(searchLower) : false) ||
      (project.title ? project.title.toLowerCase().includes(searchLower) : false)
    );
  });

  const handleViewDesign = (projectId: string) => {
    navigate(`/opensolar/design-view?projectId=${projectId}`);
  };

  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Link href="/opensolar">
            <Button variant="ghost" size="sm" className="flex items-center gap-2 mb-2">
              <ArrowLeft className="h-4 w-4" /> Back to OpenSolar
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">
            OpenSolar Projects{' '}
            {!isLoadingProjects && (
              <span className="text-2xl text-muted-foreground">({projects.length} total)</span>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage your OpenSolar design projects
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => triggerSync()}
            disabled={isSyncing}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Projects'}
          </Button>

          <Link href="/opensolar/settings">
            <Button variant="ghost" size="icon">
              <Settings className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center space-x-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search projects by name, customer, or address..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <Button variant="outline" size="icon">
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {isLoadingProjects ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No Projects Available</CardTitle>
            <CardDescription>
              There are no OpenSolar projects available. Please ensure you have configured the
              OpenSolar API correctly and have projects in your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Database className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No OpenSolar Projects Found</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                You need to create projects in OpenSolar before they can be viewed here. Make sure
                your API key is configured correctly.
              </p>
              <Link href="/opensolar/settings">
                <Button variant="secondary">Configure OpenSolar</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : filteredProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Matching Projects</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            No projects match your search criteria. Try adjusting your search terms.
          </p>
          <Button variant="outline" onClick={() => setSearchTerm('')}>
            Clear Search
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableCaption>
              A list of your OpenSolar projects. Showing {filteredProjects.length} of{' '}
              {projects.length} projects.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Project Name</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>System Size</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">{project.name}</TableCell>
                  <TableCell>
                    {project.customerName ||
                      (project.metadata && typeof project.metadata === 'string'
                        ? JSON.parse(project.metadata).originalCustomer?.name || '-'
                        : '-')}
                  </TableCell>
                  <TableCell className="max-w-xs truncate" title={project.address || ''}>
                    {project.address || '-'}
                  </TableCell>
                  <TableCell>{project.systemSize || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={project.status === 'active' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {project.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {project.updatedAt ? (
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(project.updatedAt).toLocaleDateString()}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        href={`https://app.opensolar.com/projects/${project.externalId || project.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon" title="Open in OpenSolar">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
